import { ArrowBackIos, ArrowForwardIos, Menu } from "@mui/icons-material";
import {
	Alert,
	AppBar,
	Box,
	Divider,
	FormControl,
	IconButton,
	MenuItem,
	Select,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Toolbar,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import FormsData from "../assets/forms.json";
import {
	DocumentFormData,
	Person,
	SessionState,
	SettingsObject,
} from "../utils";
import { FormWindowContainer } from "./FormWindow";

function getFormsCategories() {
	let totalCategories: string[] = [];
	Object.values(FormsData).forEach(({ categories }) =>
		categories.forEach((category) => {
			if (!totalCategories.includes(category)) {
				totalCategories.push(category);
			}
		})
	);
	return totalCategories;
}

interface FormsProps {
	isMobile: boolean;
	settings: SettingsObject;
	people: Person[];
	sessionState: SessionState;
	saveKeyPressed: boolean;
	setSessionState: (arg0: SessionState) => void;
	openDrawer: () => void;
}

export function Forms({ isMobile, openDrawer, sessionState }: FormsProps) {
	const isError = false;

	const [selectedFormId, setSelectedFormId] = useState<string | null>(null);

	const [currentCategories, setCurrentCategories] = useState<string[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(20);

	const totalCategories = getFormsCategories();
	const allForms = Object.entries(FormsData).map(([id, data]) => ({
		id,
		data,
	}));
	const currentForms = allForms.filter(
		({ data: { categories, name } }) =>
			currentCategories.filter(
				(category) => !categories.includes(category)
			).length === 0 &&
			name.toLowerCase().includes(searchTerm.toLowerCase())
	);
	const maxPages = Math.ceil(currentForms.length / pageSize);

	useEffect(() => {
		if (maxPages <= page) {
			setPage(Math.max(0, maxPages - 1));
		}
	}, [pageSize, searchTerm]);

	return (
		<>
			<AppBar
				sx={
					isMobile
						? {}
						: { left: "200px", width: "calc(100% - 200px)" }
				}
			>
				<Toolbar>
					{isMobile && (
						<Tooltip title="Open Menu">
							<IconButton
								sx={{ color: "inherit", marginRight: 1 }}
								onClick={openDrawer}
							>
								<Menu />
							</IconButton>
						</Tooltip>
					)}
					<Typography
						variant="h6"
						color="inherit"
						component="div"
						sx={{ flex: 1 }}
					>
						Forms
					</Typography>
				</Toolbar>
				{isError && (
					<Alert variant="filled" severity="error">
						An error occurred, try refreshing the page.
					</Alert>
				)}
			</AppBar>
			<Box sx={{ top: "64px", position: "relative", width: "100%" }}>
				<Box
					sx={{
						margin: 2,
						borderRadius: "4px",
						backgroundColor: (theme) => theme.palette.grey[900],
						border: (theme) =>
							`0.5px solid ${theme.palette.grey[800]}`,
					}}
				>
					<FormControl sx={{ m: 1, display: "flex" }}>
						<TextField
							sx={{ flexGrow: 1 }}
							placeholder="Search forms..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</FormControl>
					<Divider />
					<FormControl sx={{ m: 1 }}>
						<ToggleButtonGroup
							value={currentCategories}
							onChange={(_, newValue) =>
								setCurrentCategories(newValue)
							}
						>
							{totalCategories.map((category) => (
								<ToggleButton value={category}>
									{category}
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</FormControl>
				</Box>
				<div id="datagrid-start" />
				<Box
					sx={{
						position: "relative",
						border: (theme) =>
							`1px solid ${theme.palette.grey[800]}`,
						borderRadius: "4px",
						m: 1,
					}}
				>
					<TableContainer
						sx={{
							maxHeight: `calc(100vh - ${
								document
									.getElementById("datagrid-start")
									?.getBoundingClientRect().top
							}px - 84px)`,
						}}
					>
						<TableBody
							sx={{ display: "flex", flexDirection: "column" }}
						>
							{currentForms.map(({ id, data: { name } }) => (
								<TableRow
									key={id}
									sx={{
										":hover": { background: "#212121" },
										flexGrow: 1,
										display: "flex",
									}}
								>
									<TableCell
										sx={{
											flexGrow: 1,
											padding: "10px",
											cursor: "pointer",
										}}
										onClick={() => setSelectedFormId(id)}
									>
										{name}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</TableContainer>
					<Box
						sx={{
							border: (theme) =>
								`1px solid ${theme.palette.grey[800]}`,
							height: "45px",
							borderRadius: "4px",
							position: "absolute",
							bottom: "-55px",
							width: "calc(100vw - 234px)",
							display: "flex",
							alignItems: "center",
							justifyContent: "right",
						}}
					>
						<IconButton
							sx={{ float: "right", m: 1 }}
							size="small"
							onClick={() => page > 0 && setPage(page - 1)}
						>
							<ArrowBackIos />
						</IconButton>
						<Typography
							sx={{
								float: "right",
								color: (theme) => theme.palette.common.white,
							}}
						>
							{page + 1}/{maxPages}
						</Typography>
						<IconButton
							size="small"
							sx={{ float: "right", m: 1 }}
							onClick={() =>
								page < maxPages - 1 && setPage(page + 1)
							}
						>
							<ArrowForwardIos />
						</IconButton>
						<Select
							sx={{ m: 1, p: 0, fontSize: "0.75rem" }}
							size="small"
							value={pageSize}
							onChange={(e) =>
								setPageSize(Number(e.target.value))
							}
						>
							<MenuItem value={10}>10</MenuItem>
							<MenuItem value={20}>20</MenuItem>
							<MenuItem value={50}>50</MenuItem>
							<MenuItem value={100}>100</MenuItem>
						</Select>
					</Box>
				</Box>
			</Box>
			<FormWindowContainer
				formsData={
					FormsData as unknown as {
						[id: string]: DocumentFormData;
					}
				}
				selectedFormId={selectedFormId}
				sessionState={sessionState}
				onClose={() => setSelectedFormId(null)}
			/>
		</>
	);
}
